// T.A.C.C. full curriculum — single source of truth for module text, work prompt, and the Workbook questions.
// Faith-led healing journey by Rich Culmer: Through Adversity Come Change.

export const COURSE_INTRO =
  "Welcome to T.A.C.C. — Through Adversity Come Change. I'm Rich, and I'm your healer for this journey. Over the next eight modules I'll guide you through five powerful principles and three life-changing articles. This is not a normal course — this is a real healing experience. You're going to feel things. You may cry. You may get angry. That's okay — that's the point. This program was created to help you stop running from your pain, face your past, and finally heal. By the time you finish, you'll understand that God has been with you the whole time, and you'll be able to accept love, achieve love, and appreciate love — because God is love.";

export const COURSE_CLOSING =
  "Congratulations. You made it to the end of T.A.C.C. You faced your wall of adversity. You answered the hard questions. You went through the pain instead of running from it. That takes courage. You now carry knowledge, wisdom, and understanding. Your past no longer controls you. You understand that God was driving the entire time. Your job now is to live in that truth — and to help others do the same. You are now a healer too. God is love. Love is the strongest force in the universe. Go out and share it.";

export const CURRICULUM = [
  {
    id: "m1", n: "01", title: "Understanding God", focus: "The foundation · God is love", duration: "1:32",
    snippet: "The foundation of everything. Before you can heal, you face both God and yourself — and learn that He has been driving the car your entire life.",
    narration: "This is the foundation of the entire journey. Everything starts here. This principle is usually the hardest, because it forces you to face both God and yourself. God is love, and love is the strongest force in the universe. He has been driving the car your entire life — even through your hardest moments.",
    workIt: "Write a short prayer telling God exactly where you are with Him right now — honest and unfiltered.",
    questions: [
      "How would you describe your current relationship with God?",
      "When in your life did you feel like God had abandoned or forgotten you?",
      "What does the statement \u201cGod is love\u201d mean to you personally?",
      "What fears come up when you think about facing your past with God?",
      "How has your understanding of God changed over the years?",
      "In what ways have you questioned God during difficult times?",
      "Do you believe God is truly in control of your life? Why or why not?",
      "What would it look like for you to fully trust God?",
      "Write a short prayer expressing where you are with God right now.",
      "On a scale of 1 to 10, how open are you to this healing journey?",
    ],
  },
  {
    id: "m2", n: "02", title: "Accepting Where You Are", focus: "Responsibility & acceptance", duration: "1:26",
    snippet: "Wherever you are, God put you there. Stop blaming, take responsibility for the choices that brought you here — and find the power acceptance gives you.",
    narration: "Wherever you are in life right now, God put you there. You must take responsibility for the choices that brought you here. Blaming others keeps you stuck. Taking responsibility gives you power.",
    workIt: "Name one situation you\u2019ve been blaming others for — and take your part back. Say it plainly.",
    questions: [
      "Where are you currently in life emotionally, spiritually, and mentally?",
      "What specific choices did you make that contributed to where you are now?",
      "What situations have you been blaming others for?",
      "How does it feel to say \u201cGod put me here\u201d about your current situation?",
      "What would change in your life if you fully accepted where you are?",
      "What lessons do you think God wants you to learn from your current situation?",
      "In what areas of your life are you still resisting acceptance?",
      "What does taking responsibility look like for you right now?",
      "How can accepting where you are give you more peace?",
      "What is one thing you can do this week to practice acceptance?",
    ],
  },
  {
    id: "m3", n: "03", title: "Embracing Your Journey", focus: "Purpose in your story", duration: "1:18",
    snippet: "Everyone has a purpose. Embrace your whole story — the good and the bad — to discover what God has for you.",
    narration: "Everyone has a purpose. You must embrace your full journey — the good and the bad — to discover what God has for you.",
    workIt: "Write the one part of your story you\u2019ve hidden — and one way God might use it for purpose.",
    questions: [
      "What parts of your life story have you been ashamed of or tried to hide?",
      "What do you believe your God-given purpose might be?",
      "How have your struggles shaped the person you are today?",
      "If your life was a book, what would the title be and why?",
      "What events in your life do you wish never happened?",
      "What gifts or strengths have your struggles given you?",
      "How can your pain become your purpose?",
      "Are you comparing your journey to someone else\u2019s? In what way?",
      "What signs have you seen that suggest you have a specific purpose?",
      "How would your life change if you fully embraced everything you\u2019ve been through?",
    ],
  },
  {
    id: "m4", n: "04", title: "The Wall of Adversity", focus: "Facing what you hide", duration: "1:25",
    snippet: "Your fears, your traumas, the pain only you and God know about. You cannot go over it or around it. You go through it.",
    narration: "This is where the real work begins. The wall of adversity contains all your fears, shortcomings, traumas, and hidden pain that only you and God know about. You cannot run from it. You cannot go over it or under it. You must go through it.",
    workIt: "List the top 5 things on your Wall of Adversity. Just name them — you go through by naming first.",
    questions: [
      "What are the top 5 things that make up your Wall of Adversity?",
      "When these things come up, how do you normally react or cope?",
      "What emotions do you feel when facing these fears?",
      "What\u2019s one thing you\u2019ve been avoiding that you know you need to face?",
      "Why have you been running from these things for so long?",
      "How does this wall show up in your daily life?",
      "What would your life look like if this wall no longer controlled you?",
      "What\u2019s the scariest thing about facing your wall?",
      "How do you think God wants you to handle this wall?",
      "Are you truly ready to stop avoiding and start going through?",
    ],
  },
  {
    id: "m5", n: "05", title: "The Decision", focus: "The turning point", duration: "1:16",
    snippet: "The turning point. Real change begins the moment you decide to stop running and go through your pain instead.",
    narration: "This is the turning point. Real change only begins when you make a conscious decision to stop running and go through your pain.",
    workIt: "Write your commitment — to yourself and to God — that from today you stop running.",
    questions: [
      "Are you truly ready to stop running from your pain?",
      "What has running from your past already cost you in life?",
      "What would your life look like one year from now if you fully commit?",
      "What excuses have you been using to avoid facing your pain?",
      "How does it feel to make this decision right now?",
      "Who in your life might not support you making this decision?",
      "What does \u201cgoing through\u201d look like for you personally?",
      "Write your personal commitment to yourself and to God.",
      "How will you hold yourself accountable to this decision?",
      "What are you most excited about on the other side of this decision?",
    ],
  },
  {
    id: "m6", n: "06", title: "Knowledge \u2014 Facing Your Past", focus: "The first article", duration: "1:13",
    snippet: "Face your past honestly — the what, when, where, why and how — and finally stop hiding from what shaped you.",
    narration: "This is the first article. Here you face your past honestly by answering the hard questions: what, when, where, why, how, and sometimes who.",
    workIt: "Write the one painful event that still affects you most — the what, when, and where. Only what you\u2019re ready for.",
    questions: [
      "What are the major painful events that still affect you today?",
      "What exactly happened? Describe it in detail.",
      "When did these events happen?",
      "Where did these events take place?",
      "Why do you think these things happened to you?",
      "How did these events change or shape you?",
      "Who was involved? (Answer only if you\u2019re ready)",
      "What emotions come up as you write about these events?",
      "What have you never told anyone about these experiences?",
      "How do you feel now that you\u2019re finally facing these things?",
    ],
  },
  {
    id: "m7", n: "07", title: "Wisdom \u2014 Processing Your Pain", focus: "Seeing it clearly", duration: "1:20",
    snippet: "Move from raw emotion to wisdom. See your past in a clearer light — and God's hand working through all of it.",
    narration: "Now you move from raw emotion to wisdom. You begin to see your past in a new, clearer light — without being overwhelmed by the emotions.",
    workIt: "Write one lesson your pain gave you — and one way you can see God\u2019s hand in it.",
    questions: [
      "Looking back now, what did you learn from those painful experiences?",
      "In what ways did those events make you stronger or wiser?",
      "How can you see God\u2019s hand working through what you went through?",
      "What positive qualities did you develop because of your pain?",
      "How are you starting to see your past differently now?",
      "What lessons are you taking from your journey so far?",
      "What would you tell someone else going through the same pain?",
      "How has this pain prepared you to help others?",
      "What are you no longer willing to carry with you?",
      "How has your perspective changed after doing this work?",
    ],
  },
  {
    id: "m8", n: "08", title: "Understanding \u2014 Becoming the Healer", focus: "Preparation, not pain", duration: "1:19",
    snippet: "It was never random pain — it was preparation. You step into who you were made to be: a healer who helps others accept, achieve, and appreciate love.",
    narration: "This is the final stage. You now understand that everything you went through was not just random pain, but preparation. You are now becoming the healer.",
    workIt: "Write your commitment as a healer: one person you will help accept, achieve, and appreciate love.",
    questions: [
      "What is your biggest realization after completing this entire journey?",
      "How has your understanding of God, yourself, and love changed?",
      "In what ways were the things you thought were bad actually preparing you?",
      "How do you now view the statement \u201cGod is love\u201d?",
      "In what ways can you now help other people heal?",
      "What will you do differently moving forward?",
      "How will you use what you\u2019ve learned to help others?",
      "What does being a healer mean to you now?",
      "What are you most grateful for after doing this work?",
      "What is your commitment to yourself from this day forward?",
    ],
  },
];
